import { zodResolver } from '@hookform/resolvers/zod';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { AuthLayout } from '@/components/auth/AuthLayout';
import { supabase } from '@/lib/supabase';

const schema = z.object({ email: z.string().email('Enter a valid email') });
type Values = z.infer<typeof schema>;

export const Route = createFileRoute('/auth/reset-password')({
  component: ResetPasswordRoute,
});

function ResetPasswordRoute() {
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(values.email, {
      // Marker so the callback routes to /auth/update-password (set the new
      // password) instead of landing straight into the app.
      redirectTo: `${window.location.origin}/auth/callback?intent=recovery`,
    });
    if (resetError) {
      setError(resetError.message);
      return;
    }
    setSent(true);
  });

  if (sent) {
    return (
      <AuthLayout
        title="Reset email sent"
        subtitle={`If an account exists for ${form.getValues('email')}, a reset link is on its way.`}
      >
        <Link to="/auth/sign-in" className="text-sm text-oxblood hover:underline">
          Back to sign in
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Reset your password" subtitle="We'll send a link to set a new password.">
      <form onSubmit={onSubmit} noValidate className="space-y-4">
        <div>
          <label
            htmlFor="email"
            className="mb-1 block font-sans text-xs uppercase tracking-[0.12em] text-ink/60"
          >
            Email
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            {...form.register('email')}
            aria-invalid={Boolean(form.formState.errors.email)}
            className="w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none focus:ring-2 focus:ring-oxblood/30"
          />
          {form.formState.errors.email && (
            <p role="alert" className="mt-1 text-xs text-signal">
              {form.formState.errors.email.message}
            </p>
          )}
        </div>
        {error && (
          <p role="alert" aria-live="polite" className="text-sm text-signal">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={form.formState.isSubmitting}
          className="w-full rounded-md bg-ink px-4 py-2.5 font-sans text-sm font-medium text-parchment hover:bg-oxblood disabled:opacity-60"
        >
          {form.formState.isSubmitting ? 'Sending…' : 'Send reset link'}
        </button>
      </form>
      <p className="mt-6 text-center text-sm text-ink/70">
        Remembered it?{' '}
        <Link to="/auth/sign-in" className="text-oxblood hover:underline">
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
}
